import { Head, Link, useForm } from "@inertiajs/react";
import { ArrowLeft, Building2, CheckCircle2, Package, Sparkles, Tag, Truck } from "lucide-react";

import InputError from "@/components/input-error";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import AppLayout from "@/layouts/app-layout";
import { organizationsPath } from "@/routes";

export default function OrganizationNew() {
  const { data, setData, post, processing, errors, transform } = useForm({
    name: "",
    public_catalog_enabled: true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    transform((formData) => ({
      organization: formData,
    }));
    post(organizationsPath());
  };

  return (
    <AppLayout>
      <Head title="Nueva Organizacion" />
      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href={organizationsPath()}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Link>
          </Button>
          <h1 className="text-3xl font-bold">Nueva Organizacion</h1>
        </div>

        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Crear organizacion
            </CardTitle>
            <CardDescription>
              Una organizacion es tu espacio de trabajo donde gestionas compras, proveedores y equipos.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre de la organizacion *</Label>
                <Input
                  id="name"
                  placeholder="Ej: Constructora ABC"
                  value={data.name}
                  onChange={(e) => setData("name", e.target.value)}
                  disabled={processing}
                  aria-invalid={!!errors.name}
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">
                  Se generara automaticamente una URL amigable basada en este nombre.
                </p>
                <InputError message={errors.name} />
              </div>

              <label
                htmlFor="public_catalog_enabled"
                className={`block rounded-lg border-2 p-4 cursor-pointer transition-all ${
                  data.public_catalog_enabled
                    ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30"
                    : "border-muted hover:border-muted-foreground/30"
                }`}
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="public_catalog_enabled"
                    checked={data.public_catalog_enabled}
                    onCheckedChange={(checked) => setData("public_catalog_enabled", checked === true)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-emerald-600" />
                      <span className="font-semibold">
                        Empezar con el catálogo público
                      </span>
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-medium dark:bg-emerald-900 dark:text-emerald-300">
                        Recomendado
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Tu organización tendrá acceso inmediato a un catálogo completo con miles de productos,
                      proveedores y categorías del sector de la construcción.
                    </p>
                    <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Package className="h-3.5 w-3.5 text-emerald-600" />
                        +2,000 productos
                      </span>
                      <span className="flex items-center gap-1">
                        <Truck className="h-3.5 w-3.5 text-emerald-600" />
                        +50 proveedores
                      </span>
                      <span className="flex items-center gap-1">
                        <Tag className="h-3.5 w-3.5 text-emerald-600" />
                        Categorías organizadas
                      </span>
                    </div>
                    {data.public_catalog_enabled && (
                      <div className="flex items-center gap-1.5 mt-3 text-emerald-600 text-sm font-medium">
                        <CheckCircle2 className="h-4 w-4" />
                        El catálogo estará disponible al crear la organización
                      </div>
                    )}
                  </div>
                </div>
              </label>

              <div className="flex gap-3 pt-4 border-t justify-end">
                <Button
                  type="button"
                  variant="outline"
                  disabled={processing}
                  asChild
                >
                  <Link href={organizationsPath()}>Cancelar</Link>
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing ? "Creando..." : "Crear organizacion"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
